import { Field } from '@web/components/atoms/Field'
import { Input, NumberInputField, NumberInputRoot } from '@web/components/atoms/Input'
import { Box, Center, Group, HStack, VStack } from '@web/components/atoms/Layout'
import { ProfileCategory, RegisterForm as RegisterFormProps, RegisterFormSubcomponents } from '@web/graphql-components'
import { PageButton } from '../PageButton'
import { SubComponentProps } from '../utils'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { DEFAULT_REGISTER_FORM } from '../default'
import { Body2, Section2 } from '@web/components/atoms/Typography'
import { Textarea } from '@web/components/atoms/Textarea'
import { Select } from '@web/components/atoms/Select'
import { Spinner } from '@web/components/atoms/Spinner'
import { Image } from '@web/components/atoms/Image'
import FilePicker from '@web/components/molecules/FilePicker'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@web/components/atoms/Button'
import { ChevronLeftIcon, CircleTickIcon, Icon } from '@web/components/atoms/Icons'
import { RegisterProfileInput } from '@web/graphql-types'
import { Checkbox } from '@web/components/atoms/Checkbox'

export enum RegisterFormStep {
  RegisterUser = 0,
  RegisterProfile,
  Registered,
  ExistingUser,
  ExistingProfile,
}

const RegisterForm = ({
  id,
  styles,
  mobileStyles,
  SubComponent,
  subComponents,
  onSubmit,
  loading,
  extraFields,
  error,
  step = RegisterFormStep.RegisterUser,
  categories,
  formValues,
  onFileChange,
  backStep,
  isPeerToPeerGivingNotificationEnabled = false,
  ...rest
}: RegisterFormProps & {
  formValues?: Partial<RegisterProfileInput>
  step?: RegisterFormStep
  error?: string
  loading?: boolean
  categories?: ProfileCategory[]
  isPeerToPeerGivingNotificationEnabled?: boolean
  onSubmit?: (e: React.FormEvent) => void
  onFileChange?: (file: File) => void
  backStep?: () => void
  SubComponent: React.FC<SubComponentProps>
}) => {
  const components = subComponents as RegisterFormSubcomponents
  const backgroundColor = useColorWithOpacity(
    styles?.backgroundColor,
    styles?.backgroundColorOpacity,
    DEFAULT_REGISTER_FORM.styles.backgroundColor,
  )
  const borderColor = useColorWithOpacity(
    styles?.borderColor,
    styles?.borderColorOpacity,
    DEFAULT_REGISTER_FORM.styles.borderColor,
  )
  const textColor = useColorWithOpacity(styles?.textColor, '1', DEFAULT_REGISTER_FORM.styles.textColor)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [avatarFileUrl, setAvatarFileUrl] = useState('')
  const [isGivingNotificationEnabled, setIsGivingNotificationEnabled] = useState(isPeerToPeerGivingNotificationEnabled)

  useEffect(() => {
    // set this every time this setting prop changes as this form may render before the setting is loaded
    setIsGivingNotificationEnabled(isPeerToPeerGivingNotificationEnabled)
  }, [isPeerToPeerGivingNotificationEnabled])

  return (
    <VStack
      w="100%"
      className="c-component-wrapper"
      justifyContent="center"
      alignItems="center"
      asChild={true}
      gap="24px"
      bgColor={backgroundColor}
      borderRadius={`${styles?.borderRadius}px`}
      borderColor={borderColor}
      color={textColor}
      css={{
        p: mobileStyles?.padding,
        '@container page (min-width: 901px)': {
          p: styles?.padding,
        },
      }}
    >
      {step >= RegisterFormStep.Registered ? (
        <Center w="100%">
          {step === RegisterFormStep.Registered && (
            <>
              <Spinner />
              <Body2 color="ge.text.primary">Your profile is being created. Please wait...</Body2>
            </>
          )}
          {step === RegisterFormStep.ExistingUser && (
            <HStack>
              <Icon boxSize="24px">
                <CircleTickIcon />
              </Icon>
              <Box>
                <Section2>Registration Complete. Check your email.</Section2>
                <Body2>Check your email for a link to log in. If you don&apos;t see it, check your spam folder.</Body2>
              </Box>
            </HStack>
          )}
          {step === RegisterFormStep.ExistingProfile && (
            <HStack>
              <Icon boxSize="24px">
                <CircleTickIcon />
              </Icon>
              <Box>
                <Section2>This email already exists.</Section2>
                <Body2>Check your email for a link to log in. If you don&apos;t see it, check your spam folder.</Body2>
              </Box>
            </HStack>
          )}
        </Center>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()

            onSubmit?.(e)
          }}
        >
          {styles?.hasTitle && <SubComponent {...rest} id={components.title!} name="Title" parentId={id} />}
          {step === RegisterFormStep.RegisterUser && (
            <>
              <Group w="100%">
                <Field label="First Name" required={true}>
                  <Input
                    name="firstName"
                    type="text"
                    placeholder="First Name"
                    defaultValue={formValues?.firstName ?? ''}
                  />
                </Field>
                <Field label="Last Name" required={true}>
                  <Input
                    name="lastName"
                    type="text"
                    placeholder="Last Name"
                    defaultValue={formValues?.lastName ?? ''}
                  />
                </Field>
              </Group>
              <Field label="Email Address" required={true}>
                <Input name="email" type="email" placeholder="Email" defaultValue={formValues?.email ?? ''} />
              </Field>
            </>
          )}
          {step === RegisterFormStep.RegisterProfile && (
            <>
              <Box alignSelf="start">
                <Button id="back-step" buttonAppearance="secondary" onClick={() => backStep?.()} size="2xs">
                  <ChevronLeftIcon />
                  Back
                </Button>
              </Box>
              {(categories?.length ?? 0) > 1 ? (
                <Field label="Profile Type" required={true}>
                  <Select name="profileCategoryId">
                    {categories?.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <input name="profileCategoryId" type="hidden" value={categories?.[0]?.id} />
              )}
              <Field label="Profile Name" required={true}>
                <Input
                  name="profileName"
                  type="text"
                  placeholder="Enter Profile Name"
                  defaultValue={`${formValues?.firstName ?? ''} ${formValues?.lastName ?? ''}`}
                />
              </Field>
              <Field label="Goal ($)" required={true}>
                <NumberInputRoot name="goal" w="100%" min={1}>
                  <NumberInputField name="goal" placeholder="Enter Goal Amount" bgColor="ge.white" />
                </NumberInputRoot>
              </Field>
              <Group alignSelf="start">
                {avatarFileUrl && (
                  <Box w="100%" h="100%" bgColor="ge.white" borderRadius="6px" overflow="hidden">
                    <Image src={avatarFileUrl} alt="avatar" width="50px" height="50px" />
                  </Box>
                )}
                <Field label="Profile Avatar">
                  <FilePicker
                    id="profile-avatar-input"
                    inputRef={fileRef}
                    acceptedFileTypes="image/*"
                    label="Upload"
                    buttonBgColor="ge.white"
                    onChange={(e) => {
                      if (!e.target.files) {
                        return
                      }

                      const file = e.target.files[0]

                      if (!file) {
                        return
                      }

                      setAvatarFileUrl(URL.createObjectURL(file))
                      onFileChange?.(file)
                    }}
                  />
                </Field>
              </Group>
              <Field label="Description">
                <Textarea name="description" placeholder="Enter a description of your cause..." bgColor="ge.white" />
              </Field>
              {isPeerToPeerGivingNotificationEnabled && (
                <Field>
                  <input type="hidden" name="isGivingNotificationEnabled" value={`${isGivingNotificationEnabled}`} />
                  <Checkbox
                    checked={isGivingNotificationEnabled}
                    onCheckedChange={(checked) => setIsGivingNotificationEnabled(checked?.checked === true)}
                  >
                    I wish to receive an email notification whenever a donation is made
                  </Checkbox>
                </Field>
              )}
            </>
          )}
          <PageButton
            type="submit"
            name="Primary"
            buttonText={step === RegisterFormStep.RegisterProfile ? 'Register' : 'Next'}
            width="100%"
            isLoading={loading}
          />
          <Body2 color="ge.text.danger">{error}</Body2>
        </form>
      )}
    </VStack>
  )
}

export default RegisterForm
